/**
 * /downhole/run-logs/:runId — one run's drilling parameters, on its own page
 * (`GET /runs/:id`, which carries the same billing state as the list). This was
 * a panel beside the Run Logs list; it moved here when split panes were banned.
 *
 * WRITES (api#902), for every build role: Close Run on an open run, Edit, and
 * Delete, on the band.
 *
 * EDIT AND DELETE ARE DECIDED BY THE RUN'S OWN BILLING STATE (api#913):
 * `invoiced` (on a live invoice — the edit takes notes only) and
 * `ever_invoiced` (billed at any time, void included — Delete is refused for
 * good). A run read WITHOUT the two fields is "not known", so neither button
 * is offered on it — never treated as "not invoiced". The assembly's status
 * comes from the assembly read, which the edit form needs anyway for the LIH
 * picker.
 *
 * Every measured figure is imperial as stored and printed in the tenant's unit
 * system. Circulating hours are what was recorded, not the wall-clock gap
 * between run in and run out (that is trip time, a different figure).
 */
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, Pencil, Square, Trash2 } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useAuth } from '../../contexts/AuthContext';
import { useDownholeAssembly, useDownholeRun } from '../../hooks/useDownhole';
import { formatInstant } from '../../lib/calendar-date';
import { fmtCurrency, fmtHours, fmtQty } from '../../lib/display-labels';
import { DH_BILLING_TYPE_LABEL, dhLabelOrText } from '../../lib/downhole-labels';
import { canBuildDownhole } from '../../lib/downhole-roles';
import { formatDh } from '../../lib/downhole-units';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import type { DhRun, DhUnitSystem } from '../../types/downhole';
import { DH_BAND_SECONDARY, DhEmpty, DhPanel, DhSpec, RunStatePill } from './DownholeBits';
import { CloseRunDialog, DeleteRunDialog, RunFormDialog } from './RunDialogs';
import { LostInHole, wellRig } from './RunLogsScreen';
import { useBackToList } from './recordPage';

const LIST = '/downhole/run-logs';

function RunDetail({ run, units, tz, currency }: { run: DhRun; units: DhUnitSystem | null; tz: string | null; currency: string | null }) {
  const at = (v: string | null) => formatInstant(v, tz) ?? '—';
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 px-4 pt-4">
        <Link to={`/downhole/assemblies/${run.assembly_id}/build`} className="font-mono text-sm font-extrabold text-fieldiq-accent-signal hover:underline">
          {run.assembly_number || '—'}
        </Link>
        <span className="text-sm text-fieldiq-text">{run.assembly_name || ''}</span>
        <RunStatePill state={run.run_state} />
        {run.lih_flag && <LostInHole />}
      </div>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-4 p-4 sm:grid-cols-3 lg:grid-cols-4">
        <DhSpec label="Run" value={displayValue(run.run_number)} />
        <DhSpec label="Customer" value={displayValue(run.customer_name)} />
        <DhSpec label="Field tech" value={displayValue(run.field_tech_name)} />
        <DhSpec label="Run in" value={at(run.run_in_at)} />
        <DhSpec label="Run out" value={at(run.run_out_at)} />
        <DhSpec label="Well / Rig" value={wellRig(run.well_name, run.rig_name)} />
        <DhSpec label="Depth in" mono value={formatDh(run.depth_in_ft, 'length', units, { digits: 0 })} />
        <DhSpec label="Depth out" mono value={formatDh(run.depth_out_ft, 'length', units, { digits: 0 })} />
        <DhSpec label="Footage" mono value={formatDh(run.footage_drilled, 'length', units, { digits: 0 })} />
        {/* The API's figure: run out − run in. Absent until the run is closed. */}
        <DhSpec label="C&D hrs" mono value={fmtHours(run.cd_hours, 2)} />
        <DhSpec label="Circulating hrs" mono value={fmtHours(run.circulating_hours, 1)} />
        <DhSpec label="Rotating hrs" mono value={fmtHours(run.rotating_hours, 1)} />
        <DhSpec label="On-bottom hrs" mono value={fmtHours(run.on_bottom_hours, 1)} />
        <DhSpec label="ROP avg" mono value={formatDh(run.rop_avg, 'rop', units)} />
        <DhSpec label="WOB avg" mono value={formatDh(run.wob_avg_klbs, 'force', units)} />
        <DhSpec label="RPM avg" mono value={fmtQty(run.rpm_avg)} />
        <DhSpec label="Torque avg" mono value={formatDh(run.torque_avg_ftlbs, 'torque', units)} />
        <DhSpec label="Mud weight" mono value={formatDh(run.mud_weight, 'density', units)} />
        <DhSpec label="Flow rate" mono value={formatDh(run.flow_rate_gpm, 'flow', units)} />
        <DhSpec label="Bit condition" value={displayValue(run.bit_condition)} />
        <DhSpec label="Failure mode" value={displayValue(run.failure_mode)} />
        <DhSpec label="Lost in hole" value={run.lih_flag ? run.lih_component_serial || 'Yes' : run.lih_flag === false ? 'No' : '—'} />
        <DhSpec label="Billing" value={dhLabelOrText(DH_BILLING_TYPE_LABEL, run.billing_type)} />
        <DhSpec
          label="Invoice"
          value={
            run.invoiced && run.invoice_id ? (
              <Link to={`/downhole/invoices/${run.invoice_id}`} className="text-fieldiq-accent-signal hover:underline">
                {run.invoice_number || 'On an invoice'}
              </Link>
            ) : run.invoiced === false ? (
              run.ever_invoiced ? 'Billed before (voided)' : 'Not invoiced'
            ) : (
              '—'
            )
          }
        />
        <DhSpec
          label="Minimum charge"
          value={run.minimum_charge_applied ? fmtCurrency(run.minimum_charge_amount, currency) : run.minimum_charge_applied === false ? 'Not applied' : '—'}
        />
      </dl>
      {run.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{run.notes}</p>}
    </div>
  );
}

/**
 * Close / Edit / Delete for one run, on the band. Close and Edit need the
 * assembly (its string for LIH, its status for what is frozen); Edit and
 * Delete also need the run's own billing state, and a run read without it
 * gets neither.
 */
function RunActions({ run, units, tz, onDeleted }: { run: DhRun; units: DhUnitSystem | null; tz: string | null; onDeleted: () => void }) {
  const asmQuery = useDownholeAssembly(run.assembly_id);
  const asm = asmQuery.data?.data ?? null;
  const [dialog, setDialog] = useState<'edit' | 'close' | 'delete' | null>(null);
  // The assembly is the run's own (same tenant, same route family); until it
  // answers there is nothing to decide with, so nothing is offered.
  if (!asm) return null;

  const billingKnown = typeof run.invoiced === 'boolean' && typeof run.ever_invoiced === 'boolean';
  const torndown = asm.status === 'disassembled';
  const open = run.run_state === 'open';
  const canDelete = billingKnown && run.ever_invoiced === false && !torndown;

  return (
    <div className="flex flex-wrap gap-2">
      {billingKnown && (
        <button type="button" className={DH_BAND_SECONDARY} onClick={() => setDialog('edit')}>
          <Pencil className="size-4" aria-hidden="true" />
          Edit
        </button>
      )}
      {canDelete && (
        <button type="button" className={DH_BAND_SECONDARY} onClick={() => setDialog('delete')}>
          <Trash2 className="size-4" aria-hidden="true" />
          Delete
        </button>
      )}
      {open && !torndown && (
        <button type="button" className="fi-action fi-action--standard fi-action--primary" onClick={() => setDialog('close')}>
          <Square className="size-4" aria-hidden="true" />
          Close run
        </button>
      )}
      {billingKnown && (
        <RunFormDialog open={dialog === 'edit'} onClose={() => setDialog(null)} assembly={asm} run={run} invoiced={run.invoiced === true} units={units} tz={tz} />
      )}
      {open && <CloseRunDialog open={dialog === 'close'} onClose={() => setDialog(null)} run={run} units={units} tz={tz} assembly={asm} />}
      {canDelete && <DeleteRunDialog open={dialog === 'delete'} onClose={() => setDialog(null)} run={run} onDeleted={onDeleted} />}
    </div>
  );
}

export default function RunLogDetailScreen() {
  const { runId } = useParams<{ runId: string }>();
  const back = useBackToList(LIST);
  const { user } = useAuth();
  const canBuild = canBuildDownhole(user?.role);
  const query = useDownholeRun(runId);
  const failure = queryLoadFailure(query);
  const run = query.data?.data ?? null;
  const meta = query.data?.meta;
  const units = meta?.unit_system ?? null;
  const tz = meta?.time_zone ?? null;

  return (
    <div data-testid="downhole-run-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Run log"
        title={run ? `${run.assembly_number ?? '—'} · run ${run.run_number ?? '—'}` : query.isLoading ? 'Loading…' : 'Run'}
        onBack={back}
        backLabel="Back to run logs"
        actions={run && canBuild ? <RunActions run={run} units={units} tz={tz} onDeleted={back} /> : undefined}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <DhPanel eyebrow="Drilling parameters" title="Run" testId="downhole-run-detail">
            {/* A failed BACKGROUND refetch keeps the run (and any open dialog) and says so. */}
            {run ? (
              <>
                {failure && (
                  <p role="status" className="border-b border-fieldiq-border px-4 py-2 text-xs text-fieldiq-muted">
                    This run could not be refreshed just now, so it shows what loaded earlier.
                  </p>
                )}
                <RunDetail run={run} units={units} tz={tz} currency={meta?.currency ?? null} />
              </>
            ) : query.isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading run" />
              </div>
            ) : failure ? (
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this run" />
            ) : (
              <DhEmpty>This run does not exist.</DhEmpty>
            )}
          </DhPanel>
        </div>
      </section>
    </div>
  );
}
